/**
 * The Catalog run flow's pure logic: its four steps and which of them a job has reached, the
 * Results sub-views, the job's title, and the `POST /runs` bodies for continuing a sample into the
 * full run and for fixing a set of products — every run in the family shares one job id.
 */
import type {
  ContentProvider,
  GenerationRunCreate,
  GenerationRunRead,
} from '../types/content.types';
import { CONTENT_FIELDS } from '../types/content.types';

import type { CatalogJob } from './catalog-run-ids';
import { catalogRunId, nextFixNumber } from './catalog-run-ids';
import { formatCount } from './format-count';
import { platformLabel } from './platform-label';
import { isTerminalRunStatus } from './run-status';

/** The flow's pages, in order (`?step=`). */
export const CATALOG_STEPS = ['setup', 'sample', 'full', 'results'] as const;
export type CatalogStep = (typeof CATALOG_STEPS)[number];

export const CATALOG_STEP_LABELS: Record<CatalogStep, string> = {
  setup: 'Set up',
  sample: 'Sample',
  full: 'Full run',
  results: 'Results',
};

/** Results sub-views (`?rview=`); Summary is the default. */
export const CATALOG_RESULTS_VIEWS = [
  'summary',
  'products',
  'issues',
  'attributes',
  'quality',
  'gap',
] as const;
export type CatalogResultsView = (typeof CATALOG_RESULTS_VIEWS)[number];

/** Finished-sample sub-views (`?sview=`); Overview is the default. */
export const CATALOG_SAMPLE_VIEWS = ['overview', 'products', 'problems', 'gap'] as const;
export type CatalogSampleView = (typeof CATALOG_SAMPLE_VIEWS)[number];

/** Products layout on the Sample and Results (`?playout=`); Table is the default. */
export const CATALOG_PRODUCT_LAYOUTS = ['table', 'grid'] as const;
export type CatalogProductLayout = (typeof CATALOG_PRODUCT_LAYOUTS)[number];

/** The sample sizes Set up accepts — enough to judge, small enough to be cheap. */
export const SAMPLE_SIZE_MIN = 5;
export const SAMPLE_SIZE_MAX = 200;
export const SAMPLE_SIZE_DEFAULT = 40;

/** Where one step stands for a job, and why a step that can't be opened can't. */
export interface CatalogStepInfo {
  /** The step has something to show and its tab is a link. */
  reachable: boolean;
  done: boolean;
  /** The sample was skipped (the job went straight to the full run). */
  skipped: boolean;
  /** Tooltip for a step that isn't reachable yet. */
  tip: string;
}

/** Whether a run has finished one way or another (completed, failed or cancelled). */
function finished(run: GenerationRunRead | undefined): boolean {
  return !!run && isTerminalRunStatus(run.status);
}

/**
 * Each step's state for a loaded job: Set up is always done; Sample is reachable once a sample
 * run exists (skipped when the job went straight to the full run); Full run once it exists;
 * Results once the full run has finished.
 */
export function catalogStepInfo(job: CatalogJob): Record<CatalogStep, CatalogStepInfo> {
  const sampleSkipped = !job.sample && !!job.full;
  return {
    setup: { reachable: true, done: true, skipped: false, tip: '' },
    sample: {
      reachable: !!job.sample,
      done: !!job.sample && (job.sample.status === 'COMPLETED' || !!job.full),
      skipped: sampleSkipped,
      tip: sampleSkipped
        ? 'Skipped — this catalog run ran the full catalog straight away'
        : 'Starts when you start the sample',
    },
    full: {
      reachable: !!job.full,
      done: job.full?.status === 'COMPLETED',
      skipped: false,
      tip: job.sample
        ? 'Starts when you continue to the full run'
        : 'Starts after the sample, or when you run the full catalog',
    },
    results: {
      reachable: finished(job.full),
      done: false,
      skipped: false,
      tip: 'Ready when the full run finishes',
    },
  };
}

/**
 * The step a job page opens on: the requested one when it's reachable, else the furthest the
 * job has got — Results once the full run finished, Full run while it works, then Sample.
 */
export function defaultCatalogStep(job: CatalogJob, requested: CatalogStep | undefined) {
  const info = catalogStepInfo(job);
  if (requested && info[requested].reachable) return requested;
  if (info.results.reachable) return 'results';
  if (info.full.reachable) return 'full';
  if (info.sample.reachable) return 'sample';
  return 'setup';
}

/** "5 product groups" from the run's enrich list (No match isn't a chosen group); "All product groups" when empty. */
export function catalogTypesLabel(run: Pick<GenerationRunRead, 'enrichVerticals'>): string {
  const chosen = (run.enrichVerticals ?? []).filter((v) => v !== 'NO_MATCH').length;
  if (chosen === 0) return 'All product groups';
  return `${String(chosen)} ${chosen === 1 ? 'product group' : 'product groups'}`;
}

/**
 * A job's title from its setup — the service has no run name: "Google Merchant Center · 5 types ·
 * 211 products" (the channel only when known, the products only once counted).
 */
export function catalogJobTitle(run: GenerationRunRead, platform: string | null | undefined) {
  const parts = [
    ...(platform ? [platformLabel(platform)] : []),
    catalogTypesLabel(run),
    ...(run.totalProducts > 0 ? [`${formatCount(run.totalProducts)} products`] : []),
  ];
  return parts.join(' · ');
}

/**
 * The written fields to send again, from a run's recorded list: the basic five in their order, or
 * — when the run stored registry codes (a GMC / Meta flows run) — those codes as stored, which
 * `POST /runs` accepts too. Filtering codes to the five would drop them all and ask every field.
 */
export function runContentFields(run: Pick<GenerationRunRead, 'fields'>): string[] {
  const legacy = CONTENT_FIELDS.filter((field) => run.fields.includes(field));
  return legacy.length === run.fields.length ? legacy : [...new Set(run.fields)];
}

/** The settings a follow-on run repeats from an earlier run of the job. */
function sameSettings(
  source: GenerationRunRead,
  platform: ContentProvider | undefined,
): Omit<GenerationRunCreate, 'runId' | 'productFilter'> {
  const fields = runContentFields(source);
  const verticals = source.enrichVerticals ?? [];
  return {
    ...(fields.length > 0 && { fields }),
    ...(source.flows.length > 0 && { flows: source.flows }),
    ...(platform && { platform }),
    ...(source.language && { language: source.language }),
    ...(verticals.length > 0 && { enrichVerticals: verticals }),
    trigger: 'MANUAL',
  };
}

/** The full-run choices Set up made that the sample's run record can't carry back. */
export interface ContinueIntent {
  /** The product limit for the full run; null = none. */
  limit: number | null;
  regenerate: boolean;
  forceExtract: boolean;
}

/**
 * "Continue to full run": the sample's own settings read back from its run record (fields, flows,
 * types, language, product filter without the sample's `limit`) plus the Set up choices the
 * record lacks (`intent`: limit, regenerate, re-extract). With no intent (older job, another
 * browser) it takes the safe choice: skip products already written, no limit, no re-extract.
 */
export function continueToFullPayload(
  jobId: string,
  sample: GenerationRunRead,
  platform: ContentProvider | undefined,
  intent: ContinueIntent | null,
): GenerationRunCreate {
  const filter = sample.productFilter;
  const limit = intent?.limit ?? null;
  return {
    runId: catalogRunId(jobId, 'full'),
    ...sameSettings(sample, platform),
    ...(intent?.forceExtract && { forceExtract: true }),
    productFilter: {
      ...(filter.productIds.length > 0 && { productIds: filter.productIds }),
      ...(filter.brands.length > 0 && { brands: filter.brands }),
      ...(filter.categories.length > 0 && { categories: filter.categories }),
      ...(filter.updatedAfter && { updatedAfter: filter.updatedAfter }),
      ...(limit !== null && { limit }),
      includePreviouslyGenerated: intent?.regenerate ?? false,
    },
  };
}

/**
 * How many products "Continue to full run" will write when that is known: the named ids (capped
 * by the limit) or the limit alone; null for a whole-catalog / filtered run with no limit.
 */
export function continueProductCount(
  sample: GenerationRunRead,
  intent: ContinueIntent | null,
): number | null {
  const ids = sample.productFilter.productIds.length;
  const limit = intent?.limit ?? null;
  if (ids > 0) return limit !== null ? Math.min(ids, limit) : ids;
  return limit;
}

/** What one fix inside a catalog job is asked to do (`catalogFixPayload`'s input). */
export interface CatalogFixInput {
  job: CatalogJob;
  source: GenerationRunRead;
  platform: ContentProvider | undefined;
  productIds: readonly string[];
  forceExtract: boolean;
  /** Only these written fields (a per-issue fix); the source run's fields when absent. */
  fields?: readonly string[] | undefined;
  /** Types to add to the run's chosen types (products skipped because their type wasn't chosen). */
  addVerticals?: readonly string[] | undefined;
}

/**
 * A fix inside the job: the named products, written again with the source run's settings under
 * the job's next loaded fix id (`<job>-fix<n>`, probed free by `useProductFixRun`), previously
 * generated included; `forceExtract` for "Re-extract & regenerate", `fields` for one issue's field,
 * `addVerticals` to write products whose type the run left out.
 */
export function catalogFixPayload(input: CatalogFixInput): GenerationRunCreate {
  const fields = input.fields && input.fields.length > 0 ? [...input.fields] : null;
  const settings = sameSettings(input.source, input.platform);
  // A chosen-types list must also name the added types, or their products are skipped again.
  const verticals =
    settings.enrichVerticals && input.addVerticals && input.addVerticals.length > 0
      ? [...new Set([...settings.enrichVerticals, ...input.addVerticals])]
      : null;
  return {
    runId: catalogRunId(input.job.jobId, 'fix', nextFixNumber(input.job)),
    ...settings,
    ...(verticals && { enrichVerticals: verticals }),
    ...(fields && { fields }),
    ...(input.forceExtract && { forceExtract: true }),
    productFilter: { productIds: [...input.productIds], includePreviouslyGenerated: true },
  };
}
